import BaseTool from "./base/BaseTool";
import {
  getEnabledElement,
  getEnabledElements,
} from "component/toolManager/enabledElements";
// Drawing
import { getNewContext } from "../../draw/index.js";
import renderOverlayImage from "./overlayImage/renderOverlayImage";
import Cache from "core/cache/index";
import Math from "core/math";
import Helper from "helper";
import CustomEvent from "core/customEvent/index";
import makeKey from "core/cache/KeyGen";
/**
 * Internal API function to draw an image to a given enabled element
 *
 * @param {EnabledElement} enabledElement  Enabled Element to redraw
 * @param {Boolean} [invalidated = false] - true if pixel data has been invalidated and cached rendering should not be used
 * @returns {void}
 * @memberof Internal
 */
function drawImage(enabledElement, invalidated = false) {
  enabledElement.needsRedraw = true;
  if (invalidated) {
    enabledElement.invalid = true;
  }
}

/**
 * Forces the image to be updated/redrawn for the specified enabled element
 * @param {HTMLElement} element An HTML Element enabled for
 * @param {Boolean} [invalidated=false] Whether or not the image pixel data has been changed, necessitating a redraw
 *
 * @returns {void}
 * @memberof Drawing
 */
function updateImage(element, invalidated = false) {
  const enabledElement = getEnabledElement(element);

  //if (enabledElement.image === undefined && !enabledElement.layers.length) {
  if (enabledElement.image === undefined) {
    //throw new Error("updateImage: image has not been loaded yet");
    return;
  }

  drawImage(enabledElement, invalidated);
}

/**
 *
 *
 * TODO: Need to watch for configuration changes to update ToolState
 *
 * @export @public @class
 * @name ImageFusionTool
 * @classdesc Tool for image overlay
 * @extends Tools.Base.BaseTool
 */
class ImageFusionTool extends BaseTool {
  constructor(props = {}) {
    const defaultProps = {
      name: "ImageFusion",
      mixins: ["enabledOrDisabledBinaryTool"],
      configuration: {
        renderer: renderOverlayImage,
      },
    };
    //super(props, defaultProps);
    const initialConfiguration = Object.assign(defaultProps, props);

    super(initialConfiguration);

    this.initialConfiguration = initialConfiguration;
    this.renderer = null;
    this.synchronizationContext = null;
    this.fusionTarget = null;
    this.sourceElement = null;
  }

  async enabledCallback(element, { synchronizationContext } = {}) {
    const renderer = this.configuration.renderer;
    const enabledElement = getEnabledElement(element);

    if (!enabledElement || !renderer || !synchronizationContext) {
      // TODO: Unable to add tool state, image never loaded.
      // Should we `setToolDisabledForElement` here?
      // logger.warn(
      //   `Unable to enable ${this.name}. Exiting enable callback. Tool will be enabled, but will not render.`
      // );

      return;
    }
    this.sourceElement = element;
    this.renderer = renderer;
    this.synchronizationContext = synchronizationContext;

    // Fusion 대상 ELement의 WWWC 변경시 업데이트 핸들러 추가
    // Fusion 대상 Element 검색
    let metadataCache = new Cache.Metadata();
    const allEnabledElements = getEnabledElements();
    const sourceModality = enabledElement.image.modality;
    const sourceSeriesInstanceUID = enabledElement.image.seriesInstanceUID;
    const sourceKey = makeKey(sourceSeriesInstanceUID, sourceModality);
    const sourceStudyInstanceUID = metadataCache.getStudyInstanceUID(sourceKey);
    const targetEnabledElement = allEnabledElements.filter((enabledElement) => {
      if (!!enabledElement.image === false) {
        return false;
      }
      const testKey = makeKey(
        enabledElement.image.seriesInstanceUID,
        enabledElement.image.modality
      );
      const testStudyInstanceUID = metadataCache.getStudyInstanceUID(testKey);
      if (sourceStudyInstanceUID !== testStudyInstanceUID) {
        return false;
      }
      if (
        (sourceModality.toLowerCase() === "pt" &&
          enabledElement.image.modality.toLowerCase() === "ct") ||
        (sourceModality.toLowerCase() === "ct" &&
          enabledElement.image.modality.toLowerCase() === "pt")
      ) {
        return true;
      }
      return false;
    });
    if (targetEnabledElement.length === 0) {
      return;
    }
    const targetElementStub = targetEnabledElement[0];
    // 대응 Element의 WWWC 변경시, Source Element를 업데이트함.
    targetElementStub.element.addEventListener(
      CustomEvent.EVENTS.WWWC_CHANGED,
      this.handleUpdateFusionView.bind(this)
    );
    targetElementStub.element.addEventListener(
      CustomEvent.EVENTS.WWWC_CHANGING,
      this.handleUpdateFusionView.bind(this)
    );
    this.fusionTarget = targetElementStub.element;
    this.forceImageUpdate(element);
  }

  handleUpdateFusionView() {
    const enabledElement = getEnabledElement(this.sourceElement);
    CustomEvent.triggerEvent(this.sourceElement, "forceUpdate");
  }

  disabledCallback(element) {
    if (!!this.fusionTarget === true) {
      element.removeEventListener(
        CustomEvent.EVENTS.WWWC_CHANGED,
        this.handleUpdateFusionView
      );
      element.removeEventListener(
        CustomEvent.EVENTS.WWWC_CHANGING,
        this.handleUpdateFusionView
      );
    }
    this.renderer = null;
    this.forceImageUpdate(element);
  }

  forceImageUpdate(element) {
    const enabledElement = getEnabledElement(element);

    if (enabledElement.image) {
      updateImage(element);
    }
  }
  renderToolData(evt) {
    const eventData = evt.detail;

    if (!this.renderer) {
      return;
    }
    let metadataCache = new Cache.Metadata();
    const sourceModality = eventData.image.modality;
    const sourceSeriesInstanceUID = eventData.image.seriesInstanceUID;
    const sourceKey = makeKey(sourceSeriesInstanceUID, sourceModality);
    const sourceStudyInstanceUID = metadataCache.getStudyInstanceUID(sourceKey);
    const sourceElement = eventData.element;
    let isActive = false;
    for (let i = 0; i < sourceElement.classList.length; i++) {
      const className = sourceElement.classList[i];
      if (className.toLowerCase() === "active") {
        isActive = true;
      }
    }
    // if (isActive === false) {
    //   return;
    // }
    /** 1. sourceElement 와 대응되는 Element를 검색 */
    const allEnabledElements = getEnabledElements();
    const targetEnabledElement = allEnabledElements.filter((enabledElement) => {
      if (!!enabledElement.image === false) {
        return false;
      }
      const testKey = makeKey(
        enabledElement.image.seriesInstanceUID,
        enabledElement.image.modality
      );
      const testStudyInstanceUID = metadataCache.getStudyInstanceUID(testKey);
      if (sourceStudyInstanceUID !== testStudyInstanceUID) {
        return false;
      }
      if (
        (sourceModality.toLowerCase() === "pt" &&
          enabledElement.image.modality.toLowerCase() === "ct") ||
        (sourceModality.toLowerCase() === "ct" &&
          enabledElement.image.modality.toLowerCase() === "pt")
      ) {
        return true;
      }
      return false;
    });
    if (targetEnabledElement.length === 0) {
      return;
    }
    /** 2. targetElement에서 sourceElement의 현재 프레임과 대응되는 Frame을 검색 */

    const sourceImage = eventData.image;

    let sourceImagePlane = metadataCache.getImagePlaneModule(sourceKey);
    sourceImagePlane.imagePositionPatient = sourceImage.imagePosition;
    sourceImagePlane.frameOfReferenceUID = sourceImage.frameOfReferenceUID;
    if (Helper.Validator.validateImagePlaneModule(sourceImagePlane) === false) {
      // Console.log('No position found for image ' + sourceImage.imageId);

      return;
    }
    const sourceImagePosition = Math.vector.convertToVector3(
      sourceImagePlane.imagePositionPatient
    );
    const targetElementStub = targetEnabledElement[0];
    const targetSeriesKey = makeKey(
      targetElementStub.image.seriesInstanceUID,
      targetElementStub.image.modality
    );
    const stackData = metadataCache.get(targetSeriesKey).stack[0].frame;
    let minDistance = Number.MAX_VALUE;
    let newImageIdIndex = -1;

    stackData.forEach((frame, index) => {
      if (frame.imagePosition === undefined) {
        return;
      }
      const imagePosition = Math.vector.convertToVector3(frame.imagePosition);
      const distance = imagePosition.distanceToSquared(sourceImagePosition);

      if (distance < minDistance) {
        minDistance = distance;
        newImageIdIndex = index;
      }
    });

    if (newImageIdIndex === stackData.currentImageIdIndex) {
      return;
    }

    const context = getNewContext(eventData.canvas);
    Math.coordinate.setToPixelCoordinateSystem(eventData, context);

    // Render it
    this.renderer(
      context,
      eventData,
      targetElementStub.element,
      newImageIdIndex,
      eventData.element
    );
    // /**
    //  * 4. Active 상태인 DCMView와 연결된 DCMView 들을 찾는다.
    //  */
    // targetElements
    //   .filter((targetElement, index) => {
    //     let targetElementStub = getEnabledElement(targetElement);
    //     if (!targetElementStub.viewport) {
    //       return false;
    //     }
    //     return true;
    //   })
    //   .map((targetElement, index) => {
    //     let refElementStub = getEnabledElement(referenceElement);
    //     const context = getNewContext(refElementStub.canvas);
    //     Math.coordinate.setToPixelCoordinateSystem(refElementStub, context);

    //     // Render it
    //     this.renderer(context, eventData, targetElement, referenceElement);
    //   });
  }
}

export default ImageFusionTool;

// import BaseTool from "./base/BaseTool";
// import { getEnabledElement } from "component/toolManager/enabledElements";
// // Drawing
// import { getNewContext } from "../../draw/index.js";
// import renderOverlayImage from "./overlayImage/renderOverlayImage";
// import Math from "core/math";
// /**
//  * Internal API function to draw an image to a given enabled element
//  *
//  * @param {EnabledElement} enabledElement  Enabled Element to redraw
//  * @param {Boolean} [invalidated = false] - true if pixel data has been invalidated and cached rendering should not be used
//  * @returns {void}
//  * @memberof Internal
//  */
// function drawImage(enabledElement, invalidated = false) {
//   enabledElement.needsRedraw = true;
//   if (invalidated) {
//     enabledElement.invalid = true;
//   }
// }

// /**
//  * Forces the image to be updated/redrawn for the specified enabled element
//  * @param {HTMLElement} element An HTML Element enabled for
//  * @param {Boolean} [invalidated=false] Whether or not the image pixel data has been changed, necessitating a redraw
//  *
//  * @returns {void}
//  * @memberof Drawing
//  */
// function updateImage(element, invalidated = false) {
//   const enabledElement = element;

//   //if (enabledElement.image === undefined && !enabledElement.layers.length) {
//   if (enabledElement.image === undefined) {
//     //throw new Error("updateImage: image has not been loaded yet");
//     return;
//   }

//   drawImage(enabledElement, invalidated);
// }

// /**
//  *
//  *
//  * TODO: Need to watch for configuration changes to update ToolState
//  *
//  * @export @public @class
//  * @name ImageFusionTool
//  * @classdesc Tool for image overlay
//  * @extends Tools.Base.BaseTool
//  */
// class ImageFusionTool extends BaseTool {
//   constructor(props = {}) {
//     const defaultProps = {
//       name: "ImageFusion",
//       mixins: ["enabledOrDisabledBinaryTool"],
//       configuration: {
//         renderer: renderOverlayImage,
//       },
//     };
//     //super(props, defaultProps);
//     const initialConfiguration = Object.assign(defaultProps, props);

//     super(initialConfiguration);

//     this.initialConfiguration = initialConfiguration;
//     this.renderer = null;
//     this.synchronizationContext = null;
//   }

//   async enabledCallback(element, { synchronizationContext } = {}) {
//     const renderer = this.configuration.renderer;
//     const enabledElement = getEnabledElement(element);

//     if (!enabledElement || !renderer || !synchronizationContext) {
//       // TODO: Unable to add tool state, image never loaded.
//       // Should we `setToolDisabledForElement` here?
//       // logger.warn(
//       //   `Unable to enable ${this.name}. Exiting enable callback. Tool will be enabled, but will not render.`
//       // );

//       return;
//     }
//     this.renderer = renderer;
//     this.synchronizationContext = synchronizationContext;

//     this.forceImageUpdate(element);
//   }

//   disabledCallback(element) {
//     this.forceImageUpdate(element);
//   }

//   forceImageUpdate(element) {
//     const enabledElement = getEnabledElement(element);

//     if (enabledElement.image) {
//       updateImage(element);
//     }
//   }
//   renderToolData(evt) {
//     const eventData = evt.detail;

//     /**
//      * 1. 레퍼런스 라인을 그리는 렌더러 또는 동기화 Context가 초기화 되지 않았다면,
//      * 레퍼런스 라인 그리기를 중단한다.
//      */
//     if (!this.renderer || !this.synchronizationContext) {
//       return;
//     }

//     const sourceElements = this.synchronizationContext.getSourceElements();

//     /**
//      * 2. Active 상태인 DCMView를 찾고, 그 DCMView를 referenceElement로 지정한다.
//      */
//     let referenceElement = null;
//     for (let sourceElement of sourceElements) {
//       const classnameList = sourceElement.classList;

//       let foundActiveViewIdx = -1;
//       for (let i = 0; i < classnameList.length; i++) {
//         const item = classnameList[i];
//         if (typeof item !== "string" || item !== "active") {
//           continue;
//         }
//         foundActiveViewIdx = i;
//         break;
//       }

//       if (foundActiveViewIdx === -1) {
//         continue;
//       }

//       referenceElement = sourceElement;
//     }

//     /// 만약, active 상태인 Element를 찾지 못했으면 더이상 진행하지 않는다.
//     if (referenceElement === null) {
//       return;
//     }

//     /**
//      * 3. Active 상태인 DCMView와 연결된 DCMView 들을 찾는다.
//      */
//     const targetElements = this.synchronizationContext.getTargetElements(
//       referenceElement
//     );

//     /**
//      * 4. Active 상태인 DCMView와 연결된 DCMView 들을 찾는다.
//      */
//     targetElements
//       .filter((targetElement, index) => {
//         let targetElementStub = getEnabledElement(targetElement);
//         if (!targetElementStub.viewport) {
//           return false;
//         }
//         return true;
//       })
//       .map((targetElement, index) => {
//         let refElementStub = getEnabledElement(referenceElement);
//         const context = getNewContext(refElementStub.canvas);
//         Math.coordinate.setToPixelCoordinateSystem(refElementStub, context);

//         // Render it
//         this.renderer(context, eventData, targetElement, referenceElement);
//       });
//   }
// }

// export default ImageFusionTool;
